import React, { useMemo, useState } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import Page, { Content, Nav, NavGroup, NavItem } from "shared/Page";
import { Auth } from "shared/@auth/Auth";
import { ReactComponent as AudiosNavIcon } from "shared/assets/NavIcons/Audios_Nav_Icon.svg";
import { ReactComponent as DigitalDeliveryNavIcon } from "shared/assets/NavIcons/Digital_Delivery_Nav_Icon.svg";
import { ReactComponent as DisclosuresNavIcon } from "shared/assets/Disclosures_Icon.svg";
import { ReactComponent as PlaylistsNavIcon } from "shared/assets/NavIcons/Playlists_Nav_Icon.svg";
import { ReactComponent as RadioNavIcon } from "shared/assets/NavIcons/Radio_Nav_Icon.svg";
import { ReactComponent as SpeakerListNavIcon } from "shared/assets/NavIcons/Speaker_List_Nav_Icon.svg";
import { ReactComponent as SettingsNavIcon } from "shared/assets/NavIcons/Settings_Nav_Icon.svg";
import { ReactComponent as StatisticsNavIcon } from "shared/assets/NavIcons/Statistics_Nav_Icon.svg";
import Media from "./Pages/Media";
import Playlists from "./Pages/Playlists";
import DigitalDelivery from "./Pages/DigitalDelivery";
import Disclosures from "./Pages/Disclosures";
import Radio from "./Pages/Radio";
import SpeakerList from "./Pages/SpeakerList";
import Settings from "./Pages/Settings";
import Statistics from "./Pages/Statistics";
import {
  fetchDreamcastDigitalDelivery,
  fetchDreamcastDigitalDeliveryById,
  createDreamcastDigitalDelivery,
  updateDreamcastDigitalDelivery,
  duplicateDreamcastDigitalDelivery,
  setDreamcastDigitalDeliveryStatus,
} from "./api/digitalDelivery";
import { fetchDreamcastMedia, fetchDreamcastMediaFilesById } from "./api/media";
import {
  DREAMCAST_PAGE_MOCKS,
  USE_DREAMCAST_PAGE_MOCKS,
} from "./mockData/pageMocks";

// A Digital Delivery item is a MediaItem with publishState.digitalDeliveryEnabled = true.
// In production both sections share the same MediaItems DB row — the mock keeps them separate.
const DIGITAL_DELIVERY_API = {
  fetchList: fetchDreamcastDigitalDelivery,
  fetchById: fetchDreamcastDigitalDeliveryById,
  fetchFilesById: fetchDreamcastMediaFilesById,
  create: createDreamcastDigitalDelivery,
  update: updateDreamcastDigitalDelivery,
  duplicate: duplicateDreamcastDigitalDelivery,
  setArchiveState: setDreamcastDigitalDeliveryStatus,
};

// Statistics and Settings are still placeholder (mock) screens, so only
// users with the Developer/QA permission (WWG support permissions admin) see
// them; everyone else gets no nav item and the route redirects to Media.
export const DEV_PAGES_PERMISSION = "developer.all.read";

const SECTION_PATHS = {
  media: "media",
  playlists: "playlists",
  "digital-delivery": "digital-delivery",
  disclosures: "disclosures",
  radio: "radio",
  "speaker-list": "speaker-list",
  statistics: "statistics",
  settings: "settings",
};

const toDisplayStatus = (status) => {
  if (status === "archived") {
    return "Archived";
  }

  if (status === "in-review" || status === "upcoming") {
    return "In Review";
  }

  return "Available";
};

const toAudioLikeItem = (item) => ({
  mediaItemId: item.mediaItemId,
  thumbnailPath: item.thumbnailPath,
  title: item.title,
  date: item.date,
  contributorName: item.contributorName,
  sku: item.sku,
  mediaTypeCode: item.mediaTypeCode || "audio",
  typeGroup: "wwg",
  workflowStatus: item.status || "published",
  displayStatus: toDisplayStatus(item.status),
  tags: Array.isArray(item.tags) ? item.tags : [],
  isDraft: Boolean(item.isDraft),
  thumbnailBuilderData: item.thumbnailBuilderData ?? null,
  detailState: item.detailState ?? {
    title: item.title || "",
    creatorSpeaker: item.contributorName || "",
    sku: item.sku || "",
    mediaType: item.mediaTypeCode
      ? item.mediaTypeCode.charAt(0).toUpperCase() + item.mediaTypeCode.slice(1)
      : "Audio",
    isThirdParty: false,
  },
  publishState: item.publishState,
  contentSettings: item.contentSettings,
  speakers: Array.isArray(item.speakers) ? item.speakers : [],
  files: Array.isArray(item.files) ? item.files : [],
  archivedFiles: Array.isArray(item.archivedFiles) ? item.archivedFiles : [],
});

const getDigitalDeliveryEditAudios = (digitalDeliveryMocks = {}) => {
  const current = Array.isArray(digitalDeliveryMocks.currentItems)
    ? digitalDeliveryMocks.currentItems
    : [];
  const upcoming = Array.isArray(digitalDeliveryMocks.upcomingItems)
    ? digitalDeliveryMocks.upcomingItems
    : [];
  const history = Array.isArray(digitalDeliveryMocks.historyItems)
    ? digitalDeliveryMocks.historyItems
    : [];

  return [...current, ...upcoming, ...history].map(toAudioLikeItem);
};

const createDigitalDeliveryState = (digitalDeliveryMocks = {}) => ({
  currentItems: Array.isArray(digitalDeliveryMocks.currentItems)
    ? [...digitalDeliveryMocks.currentItems]
    : [],
  upcomingItems: Array.isArray(digitalDeliveryMocks.upcomingItems)
    ? [...digitalDeliveryMocks.upcomingItems]
    : [],
  historyItems: Array.isArray(digitalDeliveryMocks.historyItems)
    ? [...digitalDeliveryMocks.historyItems]
    : [],
});

const toDigitalDeliveryItem = (audioItem) => ({
  mediaItemId: audioItem.mediaItemId,
  thumbnailPath: audioItem.thumbnailPath || "",
  title: audioItem.title || "Untitled Audio",
  date: audioItem.date || new Date().toLocaleDateString("en-US"),
  contributorName: audioItem.contributorName || "No speaker",
  episode: audioItem.episodePart || "",
  sku: audioItem.sku || "N/A",
  type: audioItem.type || "Audio",
  mediaTypeCode: audioItem.mediaTypeCode || "audio",
  language: audioItem.language || "english",
  status: audioItem.workflowStatus || "published",
  isDraft: Boolean(audioItem.isDraft),
  tags: Array.isArray(audioItem.tags) ? audioItem.tags : [],
});

const upsertDigitalDeliveryItem = (currentState, audioItem) => {
  const mapped = toDigitalDeliveryItem(audioItem);
  const withoutExisting = {
    currentItems: currentState.currentItems.filter(
      (item) => item.mediaItemId !== mapped.mediaItemId,
    ),
    upcomingItems: currentState.upcomingItems.filter(
      (item) => item.mediaItemId !== mapped.mediaItemId,
    ),
    historyItems: currentState.historyItems.filter(
      (item) => item.mediaItemId !== mapped.mediaItemId,
    ),
  };

  const status = String(mapped.status || "published").toLowerCase();

  if (status === "archived") {
    return {
      ...withoutExisting,
      historyItems: [mapped, ...withoutExisting.historyItems],
    };
  }

  if (status === "upcoming" || status === "in-review") {
    return {
      ...withoutExisting,
      upcomingItems: [mapped, ...withoutExisting.upcomingItems],
    };
  }

  return {
    ...withoutExisting,
    currentItems: [mapped, ...withoutExisting.currentItems],
  };
};

export function DreamCast() {
  const { path } = useRouteMatch();
  const { hasPermission } = Auth.useContainer();
  const canSeeDevPages = hasPermission(DEV_PAGES_PERMISSION);
  const location = useLocation();
  const history = useHistory();
  const [ddEditingId, setDdEditingId] = useState(null);
  const [ddPrefetchedEditingItem, setDdPrefetchedEditingItem] = useState(null);

  const isSpeakerMediaSelection =
    location.pathname.startsWith(`${path}/media`) &&
    new URLSearchParams(location.search).get("mode") === "selection" &&
    new URLSearchParams(location.search).get("source") === "speakers";

  const activeSection = isSpeakerMediaSelection
    ? "speaker-list"
    : Object.keys(SECTION_PATHS).find((sectionKey) =>
        location.pathname.startsWith(`${path}/${SECTION_PATHS[sectionKey]}`),
      ) || "media";

  const pageMocks = USE_DREAMCAST_PAGE_MOCKS ? DREAMCAST_PAGE_MOCKS : {};

  const handleDigitalDeliveryEdit = async (id) => {
    const [detailResult, filesResult] = await Promise.allSettled([
      fetchDreamcastDigitalDeliveryById(id),
      fetchDreamcastMediaFilesById(id),
    ]);

    if (detailResult.status === "rejected") {
      throw detailResult.reason;
    }

    const detailData = detailResult.value?.data || null;
    if (!detailData) {
      throw new Error("Failed to load digital delivery details.");
    }

    const prefetchedFiles =
      filesResult.status === "fulfilled" &&
      Array.isArray(filesResult.value?.data)
        ? filesResult.value.data
        : Array.isArray(detailData.files)
        ? detailData.files
        : [];

    setDdPrefetchedEditingItem({
      ...detailData,
      files: prefetchedFiles,
    });

    setDdEditingId(id);
  };

  const handleSpeakerAudioEdit = (
    id,
    { returnPath, speakerFormState } = {},
  ) => {
    history.push({
      pathname: `${path}/media`,
      state: {
        initialEditingAudioId: id,
        speakerReturnPath: returnPath,
        speakerFormState,
      },
    });
  };

  return (
    <Page>
      <Nav>
        <NavGroup title="DreamCast">
          <NavItem
            to={`${path}/media`}
            icon={<AudiosNavIcon />}
            active={activeSection === "media"}
          >
            Media
          </NavItem>
          <NavItem
            to={`${path}/playlists`}
            icon={<PlaylistsNavIcon />}
            active={activeSection === "playlists"}
          >
            Playlists
          </NavItem>
          <NavItem
            to={`${path}/digital-delivery`}
            icon={<DigitalDeliveryNavIcon />}
            active={activeSection === "digital-delivery"}
          >
            Digital Delivery
          </NavItem>
          <NavItem
            to={`${path}/disclosures`}
            icon={<DisclosuresNavIcon />}
            active={activeSection === "disclosures"}
          >
            Disclosures
          </NavItem>
          <NavItem
            to={`${path}/radio`}
            icon={<RadioNavIcon />}
            active={activeSection === "radio"}
          >
            Radio
          </NavItem>
          <NavItem
            to={`${path}/speaker-list`}
            icon={<SpeakerListNavIcon />}
            active={activeSection === "speaker-list"}
          >
            Speaker List
          </NavItem>
          {canSeeDevPages && (
            <NavItem
              to={`${path}/statistics`}
              icon={<StatisticsNavIcon />}
              active={activeSection === "statistics"}
            >
              Statistics
            </NavItem>
          )}
          {canSeeDevPages && (
            <NavItem
              to={`${path}/settings`}
              icon={<SettingsNavIcon />}
              active={activeSection === "settings"}
            >
              Settings
            </NavItem>
          )}
        </NavGroup>
      </Nav>
      <Content>
        <Switch>
          <Route path={`${path}/media`}>
            <Media
              // Comment this spread to remove Media mocks.
              {...(pageMocks.media || pageMocks.audios || {})}
              initialEditingAudioId={
                location.state?.initialEditingAudioId || null
              }
            />
          </Route>
          <Route path={`${path}/digital-delivery/add`}>
            {/* defaultDigitalDeliveryEnabled pre-checks the "Add As Digital Delivery" toggle */}
            <Media
              headerTitle="DreamCast - Digital Delivery"
              startInCreateView
              defaultDigitalDeliveryEnabled
              addButtonLabel="Add Digital Delivery"
              apiConfig={DIGITAL_DELIVERY_API}
              onCreateCancel={() => history.push(`${path}/digital-delivery`)}
              onCreateComplete={() => history.push(`${path}/digital-delivery`)}
            />
          </Route>
          <Route path={`${path}/digital-delivery`}>
            {ddEditingId ? (
              <Media
                headerTitle="DreamCast - Digital Delivery"
                defaultDigitalDeliveryEnabled
                initialEditingAudioId={ddEditingId}
                initialAudios={
                  ddPrefetchedEditingItem ? [ddPrefetchedEditingItem] : []
                }
                preferInitialEditingData
                addButtonLabel="Add Digital Delivery"
                apiConfig={DIGITAL_DELIVERY_API}
                onCreateCancel={() => {
                  setDdEditingId(null);
                  setDdPrefetchedEditingItem(null);
                }}
                onCreateComplete={() => {
                  setDdEditingId(null);
                  setDdPrefetchedEditingItem(null);
                }}
              />
            ) : (
              <DigitalDelivery
                {...(pageMocks.digitalDelivery || {})}
                onAddNew={() => history.push(`${path}/digital-delivery/add`)}
                onEdit={handleDigitalDeliveryEdit}
              />
            )}
          </Route>
          <Route path={`${path}/disclosures`}>
            <Disclosures />
          </Route>
          <Route path={`${path}/playlists`}>
            <Playlists {...(pageMocks.playlists || {})} />
          </Route>
          <Route path={`${path}/radio`}>
            <Radio
              // Comment this spread to remove Radio mocks.
              {...(pageMocks.radio || {})}
            />
          </Route>
          <Route path={`${path}/speaker-list`}>
            <SpeakerList
              // Comment this spread to remove Speaker mocks.
              {...(pageMocks.speakers || {})}
              onEditAudio={handleSpeakerAudioEdit}
            />
          </Route>
          {canSeeDevPages && (
            <Route path={`${path}/statistics`}>
              <Statistics />
            </Route>
          )}
          {canSeeDevPages && (
            <Route path={`${path}/settings`}>
              <Settings />
            </Route>
          )}

          <Route path="*">
            <Redirect to={`${path}/media`} />
          </Route>
        </Switch>
      </Content>
    </Page>
  );
}
